import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid';
import { BsGrid3X3GapFill } from 'react-icons/bs';
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription, FaLocationArrow } from 'react-icons/fa6';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { FaFileAlt } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import { addLocationPincode, deleteLocationPincode, getLocationPincode } from '../Api';
import { BiSolidCoupon } from 'react-icons/bi';

const NAVIGATION = [
    {
        kind: 'header',
        title: 'Main items',
    },
    {
        segment: 'dashboard',
        title: 'Dashboard',
        icon: <DashboardIcon />,
    },
    {
        segment: 'dashboard-products',
        title: 'Products',
        icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-users',
        title: 'Users',
        icon: <HiUsers style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-categories',
        title: 'Categories',
        icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-orders',
        title: 'Orders',
        icon: <ShoppingCartIcon />,
    },
    {
        segment: 'prescriptions',
        title: 'Prescriptions',
        icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
    },
    {
        segment: 'dashboard-coupons',
        title: 'Coupons',
        icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-content',
        title: 'Content',
        icon: <FaFileAlt style={{ fontSize: '20px' }} />,
    },
    {
        segment: 'dashboard-location',
        title: 'Location',
        icon: <FaLocationArrow />,
    },
    {
        kind: 'divider',
    },
    {
        kind: 'header',
        title: 'Analytics',
    },
    {
        segment: 'reports',
        title: 'Reports',
        icon: <BarChartIcon />,
        children: [
            {
                segment: 'sales',
                title: 'Sales',
                icon: <DescriptionIcon />,
            },
            {
                segment: 'traffic',
                title: 'Traffic',
                icon: <DescriptionIcon />,
            },
        ],
    },
    {
        segment: 'integrations',
        title: 'Integrations',
        icon: <LayersIcon />,
    },
];

const Skeleton = styled('div')(({ theme, height }) => ({
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    height,
    content: '" "',
}));

const DeleteButton = styled(Button)(({ theme }) => ({
    height: '35px',
    width: '100px',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    '&:hover': {
        backgroundColor: theme.palette.error.dark,
    },
}));

function useDemoRouter(initialPath) {
    const [pathname, setPathname] = useState(initialPath);
    const navigate = useNavigate();
    return {
        pathname,
        searchParams: new URLSearchParams(),
        navigate: (path) => {
            setPathname(String(path));
            navigate(path);
        },
    };
}

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: '#0165AB' },
        error: { main: '#d32f2f' },
    },
});

function DshLocation({ onLogout }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const router = useDemoRouter('/dashboard-location');
    const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
    const [location, setLocation] = useState('');
    const [charge, setCharge] = useState('');
    const [pincodes, setPincodes] = useState([]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const response = await getLocationPincode();
                setPincodes(response || []);
            } catch (error) {
                console.error('Error fetching pincodes:', error);
                setError(error.response?.data?.message || 'Failed to fetch pincodes.');
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    async function addPincode() {
        if (!/^\d{6}$/.test(location)) {
            setError('Please enter a valid 6-digit pincode');
            return;
        }
        if (!charge) {
            setError('Please enter delivery charge');
            return;
        }
        setLoading(true);
        try {
            const data = { pincode: location, charge: Number(charge) };
            const response = await addLocationPincode(data);
            setPincodes([...pincodes, response?.pincode || data]);
            setLocation('');
            setCharge('');
            setError(null);
        } catch (error) {
            console.error('Error adding pincode:', error);
            setError(error.response?.data?.message || 'Failed to add pincode.');
        } finally {
            setLoading(false);
        }
    }

    async function deletePincode(pincode) {
        if (window.confirm('Are you sure you want to delete this location?')) {
            setLoading(true);
            try {
                await deleteLocationPincode(pincode);
                setPincodes(pincodes.filter((item) => item.pincode !== pincode));
                setError(null);
            } catch (error) {
                console.error('Error deleting pincode:', error);
                setError(error.response?.data?.message || 'Failed to delete pincode.');
            } finally {
                setLoading(false);
            }
        }
    }

    const handleLogoutClick = () => setOpenLogoutDialog(true);
    const handleLogoutConfirm = () => {
        setOpenLogoutDialog(false);
        onLogout();
        navigate('/');
    };
    const handleLogoutCancel = () => setOpenLogoutDialog(false);
    const handleCloseError = () => setError(null);

    const branding = {
        logo: (
            <div className="header-logo">
                {/* BUG-9: this called handleLogoutClick, so clicking the logo
                    offered to sign you out. Every other dashboard page navigates
                    home from the logo; the Logout button in ToolbarActions below
                    is the intended way out. */}
                <img
                    onClick={() => navigate('/')}
                    src={require('../images/logo.png')}
                    alt="Logo"
                    style={{ height: '52px', width: '60px', cursor: 'pointer' }}
                />
            </div>
        ),
        title: '',
    };

    const ToolbarActions = () => (
        <Button
            variant="contained"
            startIcon={<LogoutIcon />}
            onClick={handleLogoutClick}
            sx={{ marginRight: 2 }}
        >
            Logout
        </Button>
    );

    return (
        <AppProvider navigation={NAVIGATION} router={router} theme={theme} branding={branding}>
            <DashboardLayout slots={{ toolbarActions: ToolbarActions }}>
                <PageContainer>
                    <section className="admin-location">
                        <Grid container spacing={1}>
                            <Grid item size={2}>
                                <TextField
                                    label="Enter pincode"
                                    variant="outlined"
                                    value={location}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        if (value.length <= 6) {
                                            setLocation(value);
                                        }
                                    }}
                                    inputProps={{ maxLength: 6 }}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item size={2}>
                                <TextField
                                    label="Delivery Charge"
                                    variant="outlined"
                                    value={charge}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        setCharge(value);
                                    }}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item size={6}></Grid>
                            <Grid item size={2}>
                                <Button
                                    variant="contained"
                                    onClick={addPincode}
                                    fullWidth
                                    disabled={loading}
                                    sx={{ height: '40px' }}
                                >
                                    Add Pincode
                                </Button>
                            </Grid>

                            <Grid item size={12}>
                                <table className="dashboard-location-table">
                                    <thead>
                                        <tr>
                                            <th>Index</th>
                                            <th>Location</th>
                                            <th>Charge</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pincodes.length > 0 ? (
                                            pincodes.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td>{item.pincode}</td>
                                                    <td>₹{item.charge}</td>
                                                    <td>
                                                        <DeleteButton
                                                            onClick={() => deletePincode(item.pincode || item)}
                                                            disabled={loading}
                                                        >
                                                            Delete
                                                        </DeleteButton>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={4} style={{ textAlign: 'center' }}>
                                                    No pincodes available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </Grid>
                        </Grid>
                    </section>
                </PageContainer>
            </DashboardLayout>

            <Dialog open={openLogoutDialog} onClose={handleLogoutCancel}>
                <DialogTitle>Confirm Logout</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to log out?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleLogoutCancel} disabled={loading}>
                        Cancel
                    </Button>
                    <Button onClick={handleLogoutConfirm} autoFocus disabled={loading}>
                        Logout
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
                <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </AppProvider>
    );
}

export default DshLocation;
